import { useMemo } from "react";
import { addRupeeCharToFormattedAmount, dateTimeFormatter } from "services";
import { getAuctionDateTimes } from "../utils/getAuctionDateTimes";

export interface PropertyDataType {
  propertyData: any;
}

const properyDateTypes = ["undated", "private_treaty", "dated"] as const;

export type PropertyDateType = (typeof properyDateTypes)[number];

export interface AuctionStartDateTimeTypes {
  propertyDateType?: PropertyDateType;
  dateTimeString?: string;
  isAuctionConducted?: boolean;
  isEmdDateOnly?: boolean;
  isAuctionStartDateOnly?: boolean;
  isAuctionEndDateOnly?: boolean;
}

export function usePDAuctionDetails({ propertyData }: PropertyDataType) {
  const emdDate = useMemo(
    () =>
      getAuctionDateTimes({
        propertyDateType: propertyData?.property_date_type,
        dateTimeString: propertyData?.emd_last_date,
        isAuctionConducted: propertyData?.auction_by_ab,
        isEmdDateOnly: propertyData?.date_only_flags?.is_emd_date_only,
      }),
    [
      propertyData?.auction_by_ab,
      propertyData?.date_only_flags?.is_emd_date_only,
      propertyData?.emd_last_date,
      propertyData?.property_date_type,
    ],
  );

  const auctionStartDateTime = useMemo(
    () =>
      getAuctionDateTimes({
        propertyDateType: propertyData?.property_date_type,
        dateTimeString: propertyData?.auction_start_date_time,
        isAuctionConducted: propertyData?.auction_by_ab,
        isAuctionStartDateOnly:
          propertyData?.date_only_flags?.is_auction_start_date_only,
      }),
    [
      propertyData?.auction_by_ab,
      propertyData?.auction_start_date_time,
      propertyData?.date_only_flags?.is_auction_start_date_only,
      propertyData?.property_date_type,
    ],
  );

  const auctionEndDateTime = useMemo(
    () =>
      getAuctionDateTimes({
        propertyDateType: propertyData?.property_date_type,
        dateTimeString: propertyData?.auction_end_date_time,
        isAuctionConducted: propertyData?.auction_by_ab,
        isAuctionEndDateOnly:
          propertyData?.date_only_flags?.is_auction_end_date_only,
      }),
    [
      propertyData?.auction_by_ab,
      propertyData?.auction_end_date_time,
      propertyData?.date_only_flags?.is_auction_end_date_only,
      propertyData?.property_date_type,
    ],
  );

  const auctionDetails = useMemo(
    () => [
      { label: "Bank Name", value: propertyData?.bank?.name || "N/A" },
      {
        label: "Reserve Price",
        value:
          propertyData?.property_date_type !== "undated"
            ? addRupeeCharToFormattedAmount(propertyData?.reserve_price)
            : "Contact Us",
      },
      {
        label: "EMD Amount",
        value:
          propertyData?.property_date_type !== "undated"
            ? addRupeeCharToFormattedAmount(propertyData?.emd_amount)
            : "Contact Us",
      },
      { label: "EMD Last Date & Time", value: emdDate },
      { label: "Auction Start", value: auctionStartDateTime },
      { label: "Auction End", value: auctionEndDateTime },
    ],
    [
      auctionEndDateTime,
      auctionStartDateTime,
      emdDate,
      propertyData?.bank?.name,
      propertyData?.emd_amount,
      propertyData?.property_date_type,
      propertyData?.reserve_price,
    ],
  );

  return { auctionDetails };
}
